import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Breadcrumbs, Typography, Box, Menu, MenuItem, IconButton } from "@mui/material";
import SeparatorIcon from "components/Icons/SeparatorIcon";
import { useGlobalContext } from "context/GlobalContext";
import { ReactComponent as DownArrow } from "../../assets/icons/Arrowdown.svg";
import { ReactComponent as InfoIcon } from "assets/icons/Bredcrum_Icon/infoIcon.svg";
import { getClientBreadcrumbOptions } from "constants/constants";
import { useCatalogSettingContext } from "context/CatalogSettingContext";
import SortContext from "context/SortContext";
import { formatCurrency } from "utils/formatCurrency";

function ClientFlowBreadcrumbComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const { search } = React.useContext(SortContext);
  const { serarch } = useCatalogSettingContext();

  const { filterChangeCount, displayCount, filterChangeDesignCount, searchResultCount, searchSuffix, isInfoBtn, setInfoModalOpen } = useGlobalContext();

  const pathnames = location.pathname.split("/").filter((x) => x && x !== "client-flow" && x !== params.clientId);

  // --- Updated dropdown options structure ---
  const dropdownData = getClientBreadcrumbOptions(params);
  const dropdownNames = Object.keys(dropdownData);

  const activeDropdownItem = useMemo(() => {
    return Object.entries(dropdownData).find(([name, section]) =>
      section.options.some((opt) => {
        const samePath = location.pathname === opt.path;
        const sameState = location.state && opt.state && Object.keys(opt.state).every((key) => location.state[key] === opt.state[key]);
        return samePath || sameState;
      })
    );
  }, [location.pathname, location.state, dropdownData]);

  const activeDropdownName = activeDropdownItem?.[0];

  // console.log("location=====>>",location)

  // --- Determine which option label is active ---
  const activeOptionLabel = useMemo(() => {
    const section = activeDropdownItem?.[1];
    if (!section) return null;

    const matchedByState = section.options.find((opt) => location.state && opt.state && Object.keys(opt.state).every((key) => location.state[key] === opt.state[key]));

    if (!matchedByState) {
      return section.options[0]?.label;
    }

    return matchedByState.label;
  }, [location.state, activeDropdownItem]);

  // --- Build breadcrumb items (same as before) ---
  const breadcrumbItems = pathnames.map((name, index) => {
    const decodedName = decodeURIComponent(name);
    const encodedSegments = pathnames
      .slice(0, index + 1)
      .map((segment) => segment)
      .join("/");

    const routeTo = `/client-flow/${params.clientId}/${encodedSegments}`;

    const parts = decodedName.split("-");

    const formattedParts = parts
      .filter((part) => part.toLowerCase() !== "clients")
      .map((part) => {
        if (part.length >= 2) {
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        }
        return part.toUpperCase();
      });

    const hasQuotesOrders = formattedParts.includes("Quotes") && formattedParts.includes("Orders");
    const hasDuesLedger = formattedParts.includes("Dues") && formattedParts.includes("Ledger");
    const requiresCapital = formattedParts.some((part) => ["Wip", "Npd"].includes(part));

    let label;

    if (hasQuotesOrders || hasDuesLedger) {
      label = formattedParts.join(" & ");
    } else {
      label = formattedParts.join(" ");
    }

    if (requiresCapital) {
      const firstWord = formattedParts[0].toUpperCase();
      label = [firstWord, ...formattedParts.slice(1)].join(" ");
    }

    if (dropdownNames.includes(label) && label === activeDropdownName && activeOptionLabel) {
      label = activeOptionLabel;
    }
    return { name: label, path: routeTo };
  });

  const labelToDropdownKey = useMemo(() => {
    const mapping = {};
    Object.entries(dropdownData).forEach(([key, section]) => {
      section.options.forEach((option) => {
        mapping[option.label] = key;
      });
      mapping[key] = key;
    });
    return mapping;
  }, [dropdownData]);

  // --- Rest of your handlers remain the same ---
  const handleClick = (path, isLast) => {
    if (isLast) return;

    const { isActiveCatalog, isSession } = location?.state || {};

    const newState = {};
    if (isActiveCatalog !== undefined) newState.isActiveCatalog = isActiveCatalog;
    if (isSession !== undefined) newState.isSession = isSession;

    navigate(path, { state: Object.keys(newState).length ? newState : undefined });
  };

  const handleNavigate = (name, path, state) => {
    navigate(path, { state, replace: false });
    setActiveDropdown(name);
    handleCloseMenu();
  };

  const handleOpenMenu = (event, name) => {
    setAnchorEl(event.currentTarget);
    setActiveDropdown(name);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveDropdown(null);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Breadcrumbs
        aria-label="breadcrumb"
        separator={<SeparatorIcon />}
        sx={{
          "& .MuiBreadcrumbs-ol": { alignItems: "center" },
        }}
      >
        {breadcrumbItems?.map((crumb, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const dropdownKey = labelToDropdownKey[crumb.name];
          const hasDropdown = !params?.purchase && dropdownData[dropdownKey]?.isDropdown;
          const isCreateCatalog = location?.state?.isCatalog;
          if (isCreateCatalog && !isLast) {
            return;
          }

          return (
            <div key={crumb.path}>
              {hasDropdown && isLast ? (
                <>
                  <Box
                    onClick={(e) => handleOpenMenu(e, activeDropdownName || crumb.name)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 400,
                      fontSize: "20px",
                      textTransform: "capitalize",
                      color: isLast ? "text.primary" : "inherit",
                      // "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {crumb.name}
                    {displayCount && isLast && (
                      <>
                        <>
                          <span style={{ color: "#FFFFFF", fontFamily: "Poppins", fontWeight: 500, lineHeight: "36px", letterSpacing: "0px", fontSize: "20px", marginLeft: "4px" }}>{" - "}</span>
                          <span style={{ color: "#50C878", fontFamily: "Poppins", fontWeight: 500, lineHeight: "36px", letterSpacing: "0px", fontSize: "20px", marginLeft: "4px" }}>
                            {`${formatCurrency(filterChangeDesignCount)} ${searchSuffix}`}
                          </span>
                          {searchResultCount > 0 && (serarch || search) && <span style={{ color: "#FFFFFF" }}>{` (Search results: ${searchResultCount})`}</span>}
                        </>
                      </>
                    )}
                    <DownArrow
                      style={{
                        marginLeft: "20px",
                        fontSize: "100px",
                        transition: "transform 0.2s ease",
                        transform: activeDropdown === (activeDropdownName || crumb.name) ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </Box>

                  <Menu
                    anchorEl={anchorEl}
                    open={activeDropdown === (activeDropdownName || crumb.name)}
                    onClose={handleCloseMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    sx={{
                      "& .MuiPaper-root": {
                        borderRadius: "8px",
                        boxShadow: "#FFF",
                        backgroundColor: "white",
                        padding: "4px!important",
                        minWidth: "200px",
                        marginTop: "8px",
                      },
                      "& .MuiList-root": {
                        paddingTop: "0px !important",
                        paddingBottom: "0px !important",
                      },
                    }}
                  >
                    {dropdownData[activeDropdownName || crumb.name]?.options?.map((opt) => {
                      const isActive = opt.label === activeOptionLabel;
                      // console.log("opt.label=====>>",opt.label,activeOptionLabel)
                      return (
                        <MenuItem
                          key={opt.label}
                          onClick={() => handleNavigate(crumb.name, opt.path, opt.state)}
                          sx={{
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: isActive ? "#FFFFFF" : "#000000",
                            height: "41px",
                            backgroundColor: isActive ? "#444444" : "#FFF",
                            borderBottom: "1px solid #f5f5f5",
                            borderRadius: "5px",
                            "&:last-child": {
                              borderBottom: "none",
                            },
                            "&:hover": {
                              backgroundColor: "#444444",
                              color: "#FFFFFF",
                            },
                            "&.Mui-focusVisible": {
                              backgroundColor: isActive ? "#444444" : "#FFF",
                            },
                            "&.Mui-selected": {
                              backgroundColor: isActive ? "#444444" : "#FFF",
                              color: isActive ? "#FFFFFF" : "#000000",
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: "#444444",
                              color: "#FFFFFF",
                            },
                            transition: "all 0.2s ease-in-out",
                          }}
                        >
                          {opt.label}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </>
              ) : (
                <Typography
                  onClick={() => handleClick(crumb.path, isLast)}
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 400,
                    fontSize: "20px",
                    textTransform: "capitalize",
                    color: isLast ? "text.primary" : "inherit",
                    cursor: isLast ? "default" : "pointer",
                  }}
                >
                  {crumb.name}
                  {displayCount && isLast && (
                    <>
                      {filterChangeCount > 0 && ` - ${filterChangeCount}${filterChangeDesignCount > 0 ? ` (${filterChangeDesignCount} ${searchSuffix})` : " "}`}
                      {searchResultCount > 0 && (serarch || search) && ` (Search results: ${searchResultCount})`}
                    </>
                  )}
                  {isInfoBtn && isLast && (
                    <IconButton sx={{ p: 0, ml: 1 }} onClick={() => setInfoModalOpen(true)} disableRipple disableFocusRipple>
                      <InfoIcon />
                    </IconButton>
                  )}
                </Typography>
              )}
            </div>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
}

export default ClientFlowBreadcrumbComponent;
